import { createContext, useContext, useMemo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { DEMO_MODE } from '@/constants';

interface DemoModeContextValue {
  isDemoMode: boolean;
}

const DemoModeContext = createContext<DemoModeContextValue>({
  isDemoMode: false,
});

interface DemoModeProviderProps {
  children: ReactNode;
  forceDemoMode?: boolean;
}

export const DemoModeProvider = ({
  children,
  forceDemoMode,
}: DemoModeProviderProps) => {
  const location = useLocation();

  const isDemoMode = useMemo(() => {
    // Force demo mode takes highest priority (used for /demo routes)
    if (forceDemoMode !== undefined) {
      return forceDemoMode;
    }

    // URL path takes precedence: check if path starts with /demo
    if (location.pathname.startsWith('/demo')) {
      return true;
    }

    // Fall back to environment variable
    return DEMO_MODE;
  }, [forceDemoMode, location.pathname]);

  return (
    <DemoModeContext.Provider value={{ isDemoMode }}>
      {children}
    </DemoModeContext.Provider>
  );
};

export const useDemoMode = (): DemoModeContextValue => {
  const context = useContext(DemoModeContext);
  return context;
};

export default DemoModeContext;
